import React from "react";
import { cva, cx } from "class-variance-authority";

const circleVariants = cva("", {
  variants: {
    variant: {
      default: "stroke-blue-500",
      neutral: "stroke-gray-500",
      warning: "stroke-amber-400",
      error: "stroke-red-500",
      success: "stroke-emerald-500",
      destructive: "stroke-red-500",
      brand: "stroke-brand-500",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const backgroundVariants = cva("", {
  variants: {
    variant: {
      default: "stroke-blue-200",
      neutral: "stroke-gray-200",
      warning: "stroke-amber-200",
      error: "stroke-red-200",
      success: "stroke-emerald-200",
      destructive: "stroke-red-200",
      brand: "stroke-brand-200",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const ProgressCircle = React.forwardRef(
  (
    {
      value = 0,
      max = 100,
      radius = 32,
      strokeWidth = 6,
      showAnimation = true,
      variant,
      className,
      children,
      ...props
    },
    forwardedRef,
  ) => {
    const safeValue = Math.min(max, Math.max(value, 0));
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const offset = circumference - (safeValue / max) * circumference;

    const circleClass = circleVariants({ variant });
    const backgroundClass = backgroundVariants({ variant });

    return (
      <div
        className={cx("relative")}
        role="progressbar"
        aria-label="Progress circle"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        data-max={max}
        data-value={safeValue ?? null}
      >
        <svg
          ref={forwardedRef}
          width={radius * 2}
          height={radius * 2}
          viewBox={`0 0 ${radius * 2} ${radius * 2}`}
          className={cx("-rotate-90 transform", className)}
          {...props}
        >
          <circle
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            strokeWidth={strokeWidth}
            fill="transparent"
            stroke=""
            strokeLinecap="round"
            className={cx("transition-colors ease-linear", backgroundClass)}
          />
          {safeValue >= 0 ? (
            <circle
              r={normalizedRadius}
              cx={radius}
              cy={radius}
              strokeWidth={strokeWidth}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={offset}
              fill="transparent"
              stroke=""
              strokeLinecap="round"
              className={cx(
                "transition-colors ease-linear",
                circleClass,
                showAnimation &&
                  "transform-gpu transition-all duration-300 ease-in-out",
              )}
            />
          ) : null}
        </svg>
        <div
          className={cx("absolute inset-0 flex items-center justify-center")}
        >
          {children}
        </div>
      </div>
    );
  },
);

ProgressCircle.displayName = "ProgressCircle";

export default ProgressCircle;
